import styled from '@emotion/styled';
import React, { forwardRef, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ArrowsExpandOutline,
	Box,
	BoxProps,
	ClosedCaptionsOutline,
	Flex,
	HStack,
	IconButton,
	PauseNoCircleSolid,
	PlayNoCircleSolid,
	rem,
	Slider,
	SliderFilledTrack,
	SliderThumb,
	SliderTrack,
	Text,
	Tooltip,
	useBreakpointValue,
	VolumeOffOutline,
	VolumeUpOutline,
} from '@bonusly/design-system';

import { isIOSDevice } from '../../utils/browser';
import { TimeDisplay } from './time-display';

// Add webkit fullscreen type declarations
declare global {
	interface Document {
		webkitFullscreenElement: Element | null;
		webkitExitFullscreen(): void;
	}
	interface HTMLElement {
		webkitRequestFullscreen(): void;
	}
	interface HTMLVideoElement {
		webkitEnterFullscreen(): void;
	}
}

// The following is commented out because the video captions are different across browsers.
// We will itterate on them later so I'm leaving this for reference.

/* ::-webkit-media-text-track-container == no cross-browser support, this is a block-level psudo-element within the entire video element */
/* ::-webkit-media-text-track-display == no cross-browser support, this is a block-level psudo-element around the text cues */
/* ::cue == this is a inline psudo-element (the text cues themselves) */

const StyledVideoBox = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'hideNativeCaptions',
})<{ hideNativeCaptions?: boolean }>`
	/* Conditionally hide default browser captions */
	${(props) =>
		props.hideNativeCaptions &&
		`
		&::-webkit-media-text-track-container {
			display: none !important;
		}
		&::-webkit-media-text-track-display {
			display: none !important;
		}
		&::cue {
			display: none !important;
		}
	`}
`;

interface CustomVideoPlayerProps extends Omit<BoxProps, 'ref'> {
	videoId?: string;
	title?: string;
	poster?: string;
	children?: ReactNode;
	defaultMuted?: boolean;
	playVideo?: (element: HTMLVideoElement) => Promise<void>;
	pauseVideo?: (element: HTMLVideoElement) => void;
	getGlobalVolume?: () => number;
	setGlobalVolume?: (volume: number) => void;
	captions?: {
		src: string;
		label: string;
		srcLang: string;
	}[];
	allowFullScreen?: boolean;
	disableControls?: boolean;
	resetOnSourceChange?: boolean;
	hideControlsOnMouseLeave?: boolean;
	condenseVideoControlsForMobile?: boolean;
	containerProps?: BoxProps;
}

export const CustomVideoPlayer = forwardRef<HTMLVideoElement, CustomVideoPlayerProps>(
	(
		{
			videoId,
			title,
			poster,
			children,
			defaultMuted,
			playVideo,
			pauseVideo,
			getGlobalVolume,
			setGlobalVolume,
			allowFullScreen = true,
			captions,
			disableControls = false,
			resetOnSourceChange = true,
			hideControlsOnMouseLeave = true,
			condenseVideoControlsForMobile = false,
			containerProps,
			...boxProps
		},
		ref
	) => {
		const { t } = useTranslation('components');
		const [showControls, setShowControls] = useState(true);
		const [showVolumeSlider, setShowVolumeSlider] = useState(false);
		const [currentTime, setCurrentTime] = useState(0);
		const [duration, setDuration] = useState(0);
		const [isFullscreen, setIsFullscreen] = useState(false);
		const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
		const containerRef = useRef<HTMLDivElement>(null);
		const [currentCaption, setCurrentCaption] = useState<string>('');
		const [showCaptions, setShowCaptions] = useState(true);
		const [internalVolume, setInternalVolume] = useState(1);
		const previousSourceRef = useRef<string | null>(null);
		const isMobile = useBreakpointValue({ base: true, sm: false });
		// Add state to track iOS native fullscreen
		const [isIOSNativeFullscreen, setIsIOSNativeFullscreen] = useState(false);

		const isIOSWithCondensedControls = useMemo(() => {
			return condenseVideoControlsForMobile && isIOSDevice();
		}, [condenseVideoControlsForMobile]);

		// Refs to handle tracking video playback progress. In a prior iteration,
		// these were react state variables. They were updated on a loop. Updating
		// state triggered a re-render every 250 milliseconds. This is an expensive
		// operation, and it was causing other components to re-render, such as open
		// menus on the navigation bar. The result was menus that would close,
		// and sluggishness for situations where there are many videos in the feed.
		//
		// So we use these refs here instead, then update the DOM directly
		// in the onTimeUpdate event handler.
		const progressRef = useRef(0);
		// Refs for direct DOM manipulation
		const sliderRef = useRef<HTMLDivElement>(null);

		// Reset progress when source changes
		useEffect(() => {
			if (!videoElement || !resetOnSourceChange) return;

			const sourceElement = videoElement.querySelector('source');
			const currentSource = sourceElement?.src || null;

			if (previousSourceRef.current && currentSource !== previousSourceRef.current) {
				videoElement.currentTime = 0;
			}
			previousSourceRef.current = currentSource;
		}, [videoElement, resetOnSourceChange, children]);

		// Sync with global volume when global volume is provided
		useEffect(() => {
			if (getGlobalVolume && videoElement) {
				const globalVolume = getGlobalVolume();
				if (globalVolume !== videoElement.volume) {
					videoElement.volume = globalVolume;
				}
			}
		}, [getGlobalVolume, videoElement]);

		// Handle the ref callback
		useEffect(() => {
			if (typeof ref === 'function') {
				ref(videoElement);
			} else if (ref) {
				ref.current = videoElement;
			}
		}, [ref, videoElement]);

		// Format time in MM:SS format
		const formatTime = (timeInSeconds: number) => {
			const minutes = Math.floor(timeInSeconds / 60);
			const seconds = Math.floor(timeInSeconds % 60);
			return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
		};

		// Handle play/pause
		const togglePlay = () => {
			if (videoElement) {
				if (videoElement.paused) {
					if (playVideo) {
						playVideo(videoElement);
					} else {
						videoElement.play();
					}
				} else {
					if (pauseVideo) {
						pauseVideo(videoElement);
					} else {
						videoElement.pause();
					}
				}
			}
		};

		// Handle volume change
		const handleVolumeChange = (value: number) => {
			if (videoElement) {
				const newVolume = value / 100;
				videoElement.volume = newVolume;
				if (setGlobalVolume) {
					setGlobalVolume(newVolume);
				} else {
					setInternalVolume(newVolume);
				}
				videoElement.muted = newVolume === 0;
			}
		};

		// Toggle mute
		const toggleMute = () => {
			if (videoElement) {
				setShowVolumeSlider(true);
				const newMuted = !videoElement.muted;
				videoElement.muted = newMuted;
				if (newMuted) {
					// If we're muting, store the current volume
					const currentVolume = videoElement.volume;
					if (setGlobalVolume) {
						setGlobalVolume(currentVolume);
					} else {
						setInternalVolume(currentVolume);
					}
				} else {
					// If we're unmuting, restore the previous volume or use default
					const volumeToRestore = setGlobalVolume ? getGlobalVolume?.() : internalVolume;
					videoElement.volume = volumeToRestore || 0.5;
				}
			}
		};

		// Toggle mute and play for iOS condensed controls
		const toggleMuteAndPlay = () => {
			// First handle the mute/unmute using existing function
			toggleMute();

			// Then play the video if it's paused using existing function
			if (videoElement && videoElement.paused) {
				if (playVideo) {
					playVideo(videoElement);
				} else {
					videoElement.play();
				}
			}
		};

		// Handle seeking
		const handleSeek = (value: number) => {
			if (videoElement) {
				const newTime = (value / 100) * duration;
				videoElement.currentTime = newTime;

				// Update refs immediately
				progressRef.current = value;
			}
		};

		// Toggle fullscreen
		const toggleFullscreen = () => {
			if (isIOSWithCondensedControls && videoElement) {
				if (!document.webkitFullscreenElement) {
					videoElement.webkitEnterFullscreen();
					if (videoElement.paused) {
						if (playVideo) {
							playVideo(videoElement);
						} else {
							videoElement.play();
						}
					}
				} else {
					document.webkitExitFullscreen();
				}
			} else if (containerRef.current) {
				if (!document.fullscreenElement && !document.webkitFullscreenElement) {
					if (containerRef.current.requestFullscreen) {
						containerRef.current.requestFullscreen().catch((err) => {
							console.error(`Error attempting to enable fullscreen: ${err.message}`);
						});
					} else if (containerRef.current.webkitRequestFullscreen) {
						containerRef.current.webkitRequestFullscreen();
					}
				} else {
					if (document.exitFullscreen) {
						document.exitFullscreen();
					} else if (document.webkitExitFullscreen) {
						document.webkitExitFullscreen();
					}
				}
			}
		};

		// Toggle captions
		const toggleCaptions = () => {
			setShowCaptions(!showCaptions);
		};

		// Add iOS fullscreen detection useEffect
		useEffect(() => {
			const video = videoElement;
			if (!video) return;

			const handleIOSFullscreenEnter = () => {
				setIsIOSNativeFullscreen(true);
			};

			const handleIOSFullscreenExit = () => {
				setIsIOSNativeFullscreen(false);
			};

			// Listen on VIDEO element, not document
			video.addEventListener('webkitbeginfullscreen', handleIOSFullscreenEnter);
			video.addEventListener('webkitendfullscreen', handleIOSFullscreenExit);

			return () => {
				video.removeEventListener('webkitbeginfullscreen', handleIOSFullscreenEnter);
				video.removeEventListener('webkitendfullscreen', handleIOSFullscreenExit);
			};
		}, [videoElement]);

		// Update custom captions
		useEffect(() => {
			const video = videoElement;
			if (!video || !captions?.length) return;

			// Disable all tracks first
			for (let i = 0; i < video.textTracks.length; i++) {
				video.textTracks[i].mode = 'disabled';
			}

			if (isIOSNativeFullscreen) {
				const track = video.textTracks[0];
				track.mode = 'showing';
			} else {
				const track = video.textTracks[0];
				if (!track) return;

				// Enable track in hidden mode to receive cuechange events but hide native captions
				track.mode = 'hidden';

				const updateCaption = (e: Event) => {
					const track = e.target as TextTrack;
					const cue = track.activeCues?.[0] as VTTCue | undefined;
					if (cue) {
						setCurrentCaption(cue.text);
					} else {
						setCurrentCaption('');
					}
				};

				track.addEventListener('cuechange', updateCaption);
				return () => track.removeEventListener('cuechange', updateCaption);
			}
		}, [captions, videoElement, isIOSNativeFullscreen]);

		// Update progress as video plays
		useEffect(() => {
			const video = videoElement;
			if (!video) return;

			const onTimeUpdate = () => {
				const rect = videoElement.getBoundingClientRect();

				const isPartiallyVisible = rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
				if (!isPartiallyVisible) return;

				// Duration Metadata for stream is not available until the video is loaded
				if (video.duration && Number.isFinite(video.duration)) {
					if (duration !== video.duration) {
						setDuration(video.duration); // Only update React state once when duration changes
					}
				}

				// Update refs (no re-render)
				const newProgress = (video.currentTime / video.duration) * 100;
				progressRef.current = newProgress;

				// Update current time state only if the formatted time has changed (reduces re-renders)
				const newFormattedTime = Math.floor(video.currentTime);
				const currentFormattedTime = Math.floor(currentTime);
				if (newFormattedTime !== currentFormattedTime) {
					setCurrentTime(video.currentTime);
				}

				// Direct DOM manipulation - update progress bar without React
				const sliderInput = sliderRef.current;
				if (sliderInput && !isNaN(newProgress)) {
					// The ref points to the hidden input, we need the parent container
					const slider = sliderInput.parentElement as HTMLElement;

					// Target the correct Chakra UI elements based on actual DOM structure
					// This allows us to set slider and clock values without re-rendering
					// the component, which is copacetic with other components that are sensitive
					// to frequent re-renders.
					const filledTrack = slider.querySelector('.chakra-slider__filled-track') as HTMLElement;
					const thumb = slider.querySelector('.chakra-slider__thumb') as HTMLElement;

					// Update the filled track width (this is what creates the visual progress)
					if (filledTrack) {
						filledTrack.style.width = `${newProgress}%`;
					}

					// Update thumb position (if it exists)
					if (thumb) {
						const newLeft = `calc(${newProgress}% - var(--slider-thumb-size) / 2)`;
						thumb.style.left = newLeft;
					}

					// Update ARIA attributes for accessibility direclty on the DOM, without
					// triggering a React re-render.
					const sliderRole = slider.querySelector('[role="slider"]') as HTMLElement;
					if (sliderRole) {
						sliderRole.setAttribute('aria-valuenow', Math.round(newProgress).toString());
						sliderRole.setAttribute('aria-valuetext', `${formatTime(currentTime)} of ${formatTime(duration)}`);
					}
				}
			};

			const onLoadedMetadata = () => {
				if (Number.isFinite(video.duration)) {
					setDuration(video.duration);
				} else {
					setDuration(0);
				}
			};

			const onFullscreenChange = () => {
				const newFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
				setIsFullscreen(newFullscreen);

				// Only handle volume state, don't affect playback
				if (video) {
					video.volume = video.volume || 0.5;
					video.muted = video.muted ?? false;
				}
			};

			video.addEventListener('timeupdate', onTimeUpdate);
			video.addEventListener('loadedmetadata', onLoadedMetadata);
			document.addEventListener('fullscreenchange', onFullscreenChange);
			document.addEventListener('webkitfullscreenchange', onFullscreenChange);

			return () => {
				video.removeEventListener('timeupdate', onTimeUpdate);
				video.removeEventListener('loadedmetadata', onLoadedMetadata);
				document.removeEventListener('fullscreenchange', onFullscreenChange);
				document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
			};
		}, [videoElement, showControls, isFullscreen]);

		// Update the click handler in the component
		const handleVideoClick = (e: React.MouseEvent) => {
			e.stopPropagation();
			if (isIOSWithCondensedControls) {
				toggleFullscreen();
			} else {
				togglePlay();
			}
		};

		const getCaptionWidth = (caption: string) => {
			return caption.length < 60 ? 'fit-content' : '80%';
		};

		return (
			<Box
				ref={containerRef}
				position="relative"
				paddingBottom="56.25%"
				onMouseEnter={() => {
					if (disableControls || !hideControlsOnMouseLeave) return;
					setShowControls(true);
				}}
				onMouseLeave={() => {
					if (hideControlsOnMouseLeave) {
						setShowControls(false);
						setShowVolumeSlider(false);
					}
				}}
				onClick={handleVideoClick}
				role="region"
				aria-label={`Video player${title ? `: ${title}` : ''}`}
				_after={
					isIOSWithCondensedControls
						? {}
						: {
								content: '""',
								position: 'absolute',
								bottom: 0,
								left: 0,
								right: 0,
								height: '100px',
								background: 'linear-gradient(180deg, rgba(20, 21, 26, 0) 0%, rgba(20, 21, 26, 0.9) 100%)',
								pointerEvents: 'none',
								zIndex: 1,
								borderRadius: 'md',
								opacity: showControls && !disableControls ? 1 : 0,
								transition: 'opacity 0.3s',
							}
				}
				overflow="hidden"
				borderRadius="md"
				{...containerProps}
			>
				<StyledVideoBox
					as="video"
					id={`video-${videoId}`}
					title={title}
					poster={poster}
					ref={setVideoElement}
					controls={false}
					controlsList="nodownload nofullscreen noremoteplayback"
					muted={defaultMuted}
					playsInline={true}
					disablePictureInPicture={true}
					disableRemotePlayback={true}
					crossOrigin="anonymous"
					borderRadius="md"
					backgroundColor="black"
					cursor="pointer"
					objectFit="contain"
					position="absolute"
					top="0"
					left="0"
					width="100%"
					height="100%"
					transform="translateZ(0)"
					aria-label={title || 'Video content'}
					hideNativeCaptions={!isIOSNativeFullscreen}
					{...boxProps}
				>
					{children}
					{captions?.map((caption, index) => (
						<track
							key={index}
							src={caption.src}
							kind="subtitles"
							srcLang={caption.srcLang}
							label={caption.label}
							default={index === 0}
							style={{ display: 'none' }}
						/>
					))}
				</StyledVideoBox>

				{/* Custom captions overlay */}
				{showCaptions && currentCaption && captions && captions.length > 0 && (
					<Text
						position="absolute"
						bottom="10%"
						left="50%"
						width={isFullscreen ? 'auto' : getCaptionWidth(currentCaption)}
						transform="translateX(-50%)"
						bg="rgba(0, 0, 0, 0.8)"
						color="text.onColor"
						px={rem(20)}
						py={rem(8)}
						fontWeight="extrabold"
						borderRadius="md"
						textAlign="center"
						fontFamily="var(--chakra-fonts-display)"
						fontSize={isMobile ? (isFullscreen ? '1rem' : '0.75rem') : isFullscreen ? '1.5rem' : '1rem'}
						whiteSpace="pre-wrap"
						overflowWrap="break-word"
						role="status"
						aria-live="polite"
					>
						{currentCaption}
					</Text>
				)}

				{/* Controls overlay */}
				<Flex
					position="absolute"
					bottom="0"
					left="0"
					right="0"
					px={rem(16)}
					py={rem(13)}
					color="white"
					borderRadius="md"
					alignItems="center"
					transition="opacity 0.3s"
					opacity={showControls && !disableControls ? 1 : 0}
					direction="column"
					onClick={(e) => e.stopPropagation()}
					zIndex={2}
					role="toolbar"
					aria-label="Video controls"
				>
					{/* Progress bar - only show on non-iOS devices */}
					{!isIOSWithCondensedControls && (
						<Slider
							ref={sliderRef}
							aria-label="Video progress"
							value={0}
							onChange={handleSeek}
							min={0}
							max={100}
							p={0}
							width="100%"
							mb={isFullscreen ? rem(10) : rem(8)}
						>
							<SliderTrack bg="whiteAlpha.300" height={rem(6)}>
								<SliderFilledTrack bg="text.onColor" />
							</SliderTrack>
							<SliderThumb boxSize={3} _focusVisible={{ boxShadow: 'none' }} />
						</Slider>
					)}

					<Flex width="100%" justifyContent="space-between" alignItems="center">
						{/* Left side controls - only show on non-iOS devices */}
						{!isIOSWithCondensedControls && (
							<HStack spacing={isFullscreen ? 4 : 2}>
								{/* Play/Pause button */}
								<Tooltip label={videoElement?.paused ? t('video.play') : t('video.pause')} placement="top">
									<IconButton
										aria-label={videoElement?.paused ? 'Play video' : 'Pause video'}
										icon={videoElement?.paused ? <PlayNoCircleSolid size="sm" /> : <PauseNoCircleSolid size="sm" />}
										onClick={togglePlay}
										variant="ghost"
										color="white"
										_hover={{ bg: 'whiteAlpha.300' }}
										size={isFullscreen ? 'md' : 'sm'}
										minW={6}
										minH={6}
										title={isFullscreen ? (videoElement?.paused ? t('video.play') : t('video.pause')) : undefined}
									/>
								</Tooltip>
								<TimeDisplay currentTime={currentTime} duration={duration} />
							</HStack>
						)}

						<HStack spacing={isFullscreen ? 4 : 2} ml="auto">
							{/* Volume control - only show on non-iOS devices */}
							{!isIOSWithCondensedControls && (
								<Flex alignItems="center" position="relative">
									<Box
										position="absolute"
										right="100%"
										mr={2}
										opacity={showVolumeSlider ? 1 : 0}
										transition="opacity 0.2s"
										display={{ base: 'none', sm: 'block' }}
										onMouseEnter={() => setShowVolumeSlider(true)}
										onMouseLeave={() => setShowVolumeSlider(false)}
									>
										<Slider
											aria-label="Volume control"
											value={videoElement?.muted ? 0 : (videoElement?.volume ?? 0) * 100}
											onChange={handleVolumeChange}
											min={0}
											max={100}
											width={isFullscreen ? '80px' : '60px'}
										>
											<SliderTrack bg="rgba(255, 255, 255, 0.24)" height={isFullscreen ? 2 : 1}>
												<SliderFilledTrack bg="text.onColor" />
											</SliderTrack>
											<SliderThumb boxSize={3} _focusVisible={{ boxShadow: 'none' }} />
										</Slider>
									</Box>
									<Tooltip label={videoElement?.muted ? t('video.unmute') : t('video.mute')} placement="top">
										<IconButton
											aria-label={videoElement?.muted ? 'Unmute video' : 'Mute video'}
											icon={videoElement?.muted ? <VolumeOffOutline size="sm" /> : <VolumeUpOutline size="sm" />}
											onClick={toggleMute}
											onMouseEnter={() => setShowVolumeSlider(true)}
											cursor="pointer"
											variant="ghost"
											color="white"
											_hover={{ bg: 'whiteAlpha.300' }}
											size={isFullscreen ? 'md' : 'sm'}
											title={isFullscreen ? (videoElement?.muted ? t('video.unmute') : t('video.mute')) : undefined}
										/>
									</Tooltip>
								</Flex>
							)}

							{/* Mute/Unmute button for iOS */}
							{isIOSWithCondensedControls && (
								<Tooltip label={videoElement?.muted ? t('video.unmute') : t('video.mute')} placement="top">
									<IconButton
										aria-label={videoElement?.muted ? 'Unmute video' : 'Mute video'}
										icon={videoElement?.muted ? <VolumeOffOutline size="sm" /> : <VolumeUpOutline size="sm" />}
										onClick={toggleMuteAndPlay}
										variant="ghost"
										color="white"
										rounded="full"
										bg="blackAlpha.800"
										_hover={{ bg: 'blackAlpha.800' }}
										size="md"
									/>
								</Tooltip>
							)}

							{/* Captions indicator */}
							{captions && captions.length > 0 && !isIOSWithCondensedControls && (
								<Tooltip label={showCaptions ? t('video.hide_captions') : t('video.show_captions')} placement="top">
									<IconButton
										aria-label={showCaptions ? 'Hide captions' : 'Show captions'}
										icon={showCaptions ? <ClosedCaptionsOutline size="sm" /> : <ClosedCaptionsOutline size="sm" />}
										onClick={toggleCaptions}
										variant="ghost"
										color="white"
										_hover={{ bg: 'whiteAlpha.300' }}
										size={isFullscreen ? 'md' : 'sm'}
										title={isFullscreen ? (showCaptions ? t('video.hide_captions') : t('video.show_captions')) : undefined}
									/>
								</Tooltip>
							)}

							{/* Fullscreen button - only show on non-iOS devices */}
							{allowFullScreen && !isIOSWithCondensedControls && (
								<Tooltip label={isFullscreen ? t('video.minimize') : t('video.fullscreen')} placement="top">
									<IconButton
										aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
										icon={<ArrowsExpandOutline size="sm" />}
										onClick={toggleFullscreen}
										variant="ghost"
										color="white"
										_hover={{ bg: 'whiteAlpha.300' }}
										size={isFullscreen ? 'md' : 'sm'}
										title={isFullscreen ? t('video.minimize') : undefined}
									/>
								</Tooltip>
							)}
						</HStack>
					</Flex>
				</Flex>
			</Box>
		);
	}
);

CustomVideoPlayer.displayName = 'CustomVideoPlayer';
